import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Check, SpinnerGap } from '@phosphor-icons/react';
import { toast } from 'react-toastify';
import { v4 as uuidv4 } from 'uuid';
import { Button } from '@/components/ui/button';
import { useAssetCategories, useCreateAsset, useAssetsByType } from 'hooks/useAssets';
import { useBuildingsAndUnits } from 'hooks/useBuildingsAndUnits';
import { AssetCategory, AssetType, AssetStatus } from 'shared/types/asset';
import CategoryTypeSelector from './bulk-creator/CategoryTypeSelector';
import LocationSourcePanel from './bulk-creator/LocationSourcePanel';
import InstancesTargetPanel from './bulk-creator/InstancesTargetPanel';
import { LocationItemData } from './bulk-creator/DraggableLocationItem';

const AssetBulkCreatorPage: React.FC = () => {
  const navigate = useNavigate();
  const { categories, isLoading: categoriesLoading, refetch: refetchCategories } = useAssetCategories();
  const { buildings, units } = useBuildingsAndUnits();
  const { createAsset } = useCreateAsset();

  const [selectedCategory, setSelectedCategory] = useState<AssetCategory | null>(null);
  const [selectedType, setSelectedType] = useState<AssetType | null>(null);
  const [createdAssetIds, setCreatedAssetIds] = useState<Set<string>>(new Set());
  const [isBulkCreating, setIsBulkCreating] = useState(false);

  // Fetch existing assets for the selected type
  const {
    assets: existingAssets,
    isLoading: assetsLoading,
    refetch: refetchAssets,
  } = useAssetsByType(
    selectedCategory?.id ?? null,
    selectedType?.id ?? null
  );

  const handleSelectCategory = (category: AssetCategory) => {
    setSelectedCategory(category);
    setSelectedType(null);
    setCreatedAssetIds(new Set());
  };

  const handleSelectType = (type: AssetType) => {
    setSelectedType(type);
    setCreatedAssetIds(new Set());
  };

  const handleTypeCreated = async () => {
    await refetchCategories();
    // Update selectedCategory reference to the fresh one (with the new type included)
    if (selectedCategory) {
      const fresh = categories.find(c => c.id === selectedCategory.id);
      if (fresh) setSelectedCategory(fresh);
    }
  };

  const handleCategoryCreated = async (newCategory: AssetCategory) => {
    await refetchCategories();
    setSelectedCategory(newCategory);
    setSelectedType(null);
    setCreatedAssetIds(new Set());
  };

  const handleBulkCreate = useCallback(
    async (items: LocationItemData[]) => {
      if (!selectedCategory || !selectedType) {
        toast.error('Selecteer eerst een categorie en type');
        return;
      }

      if (items.length === 0) return;

      setIsBulkCreating(true);

      try {
        const newIds: string[] = [];
        let successCount = 0;
        let errorCount = 0;

        for (const item of items) {
          try {
            const newAsset = await createAsset({
              id: uuidv4(),
              assetCategoryId: selectedCategory.id,
              assetTypeId: selectedType.id,
              buildingId: item.buildingId || undefined,
              unitId: item.unitId || undefined,
              name: selectedType.name,
              status: AssetStatus.PENDING_INSTALLATION,
              archived: false,
            });
            newIds.push(newAsset.id);
            successCount++;
          } catch (err) {
            errorCount++;
            console.error(`Error creating asset for ${item.label}:`, err);
          }
        }

        if (newIds.length > 0) {
          setCreatedAssetIds((prev) => new Set([...prev, ...newIds]));
          await refetchAssets();
        }

        if (errorCount === 0) {
          toast.success(
            `${successCount} ${selectedType.name} aangemaakt`
          );
        } else if (successCount > 0) {
          toast.warning(
            `${successCount} van ${items.length} voorzieningen aangemaakt (${errorCount} mislukt)`
          );
        } else {
          toast.error('Fout bij aanmaken van voorzieningen');
        }
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Fout bij bulk aanmaken';
        toast.error(errorMessage);
        console.error('Error bulk creating assets:', err);
      } finally {
        setIsBulkCreating(false);
      }
    },
    [selectedCategory, selectedType, createAsset, refetchAssets]
  );

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      {/* Header bar */}
      <div className="flex items-center justify-between px-6 py-4 border-b bg-background">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/assets')}
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Terug
          </Button>
          <h1 className="text-lg font-semibold">Voorzieningen toewijzen</h1>
        </div>
        <Button
          variant="default"
          size="sm"
          onClick={() => navigate('/assets')}
        >
          <Check className="h-4 w-4 mr-1" />
          Klaar
        </Button>
      </div>

      {/* Category/Type selector */}
      <div className="px-6 py-3 border-b bg-muted/30">
        {categoriesLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <SpinnerGap className="h-4 w-4 animate-spin" />
            Categorieën laden...
          </div>
        ) : (
          <CategoryTypeSelector
            categories={categories}
            selectedCategory={selectedCategory}
            selectedType={selectedType}
            onSelectCategory={handleSelectCategory}
            onSelectType={handleSelectType}
            onTypeCreated={handleTypeCreated}
            onCategoryCreated={handleCategoryCreated}
          />
        )}
      </div>

      {/* Main split view */}
      <div className="flex-1 flex min-h-0">
        {/* Left panel - locations */}
        <div className="w-1/2 border-r overflow-hidden">
          {selectedType ? (
            <LocationSourcePanel
              scopes={selectedType.scopes}
              buildings={buildings}
              units={units}
              existingAssets={existingAssets}
              onBulkCreate={handleBulkCreate}
              isCreating={isBulkCreating}
            />
          ) : (
            <div className="flex items-center justify-center h-full p-6">
              <p className="text-sm text-muted-foreground text-center">
                Selecteer een categorie en type om locaties te zien
              </p>
            </div>
          )}
        </div>

        {/* Right panel - instances */}
        <div className="w-1/2 overflow-hidden">
          <InstancesTargetPanel
            assets={existingAssets}
            isLoading={assetsLoading}
            hasTypeSelected={!!selectedType}
            createdAssetIds={createdAssetIds}
          />
        </div>
      </div>
    </div>
  );
};

export default AssetBulkCreatorPage;
